import { computed } from 'vue'
import { useQuery } from '@tanstack/vue-query'
import store from '@/store'
import { v2DepartmentFindQueryOptions } from '@/composables/queries/v2department/use-v2-department-find-query'
import { v2EmployeeFindQueryOptions } from '@/composables/queries/v2employee/use-v2-employee-find-query'
import { ApiV2DepartmentElFindParams } from '@/apis/swaggers/v2/v2-swagger'

/*
 * 근무현황(WorkStatePopup)의 부서/직원 목록 — 구 ManagingDataController 대체.
 *
 * - 서버 상태: vue-query 구독 (부서 → 부서 ID 기반 직원 체인 조회).
 *   부서/직원 mutation 훅의 invalidation 이 자동 재조회를 트리거한다.
 * - isSelected(근무표 필터 선택): Vuex persisted(V2Department/V2Employee)에 보관하고
 *   computed 에서 병합 — 컨트롤러가 하던 로컬 배열 이중 관리를 제거.
 * - 레거시 조회 파라미터(isRemoved $ne — 필드 없는 구 데이터 포함)를 유지한다.
 */

const WORKER_LIST_PARAMS = { isRemoved: { $ne: true }, way: 1, key: 'sortNum' }
const CACHE_OPTIONS = { staleTime: 5 * 60 * 1000, gcTime: 5 * 60 * 1000 }

type SelectableItem = { _id?: string; isSelected?: boolean; sortNum?: number }

export function useManagingWorkers() {
  const hospitalID = () => store.state.hospital.selectedHospital?._id

  const { data: departmentsData } = useQuery(() => ({
    ...v2DepartmentFindQueryOptions({
      hospitalID: hospitalID(),
      ...WORKER_LIST_PARAMS,
    } as unknown as ApiV2DepartmentElFindParams),
    enabled: !!hospitalID(),
    ...CACHE_OPTIONS,
  }))

  const { data: employeesData } = useQuery(() => {
    const departmentIDs = ((departmentsData.value as SelectableItem[]) ?? []).map((d) => d._id)
    return {
      ...v2EmployeeFindQueryOptions({
        hospitalID: hospitalID(),
        ...WORKER_LIST_PARAMS,
        departmentID: departmentIDs,
      } as unknown as ApiV2DepartmentElFindParams),
      enabled: !!hospitalID() && departmentIDs.length > 0,
      ...CACHE_OPTIONS,
    }
  })

  const mergeSelection = (items: SelectableItem[] | undefined, storedKey: 'V2Department' | 'V2Employee') => {
    const id = hospitalID()
    const stored: SelectableItem[] = (id && store.state[storedKey].hospitals[id]) || []
    // 얕은 복사로 충분 — 소비처는 최상위 isSelected 만 변형하고 중첩 객체는 읽기 전용.
    // (이 composable 은 캘린더 셀 등 수백 인스턴스에서 호출되므로 deepCopy 는 렌더 폭풍을 만든다)
    return (items ?? []).map((item) => ({
      ...item,
      isSelected: stored.find((s) => s._id === item._id)?.isSelected ?? true,
    }))
  }

  const departments = computed(() => mergeSelection(departmentsData.value as SelectableItem[], 'V2Department'))
  const employees = computed(() => mergeSelection(employeesData.value as SelectableItem[], 'V2Employee'))

  // NOTE: 구 컨트롤러의 "목록 도착 시 persisted Set 재동기화"는 하지 않는다 —
  // 수백 인스턴스가 이 composable 을 호출하므로 watch→commit 폭풍(freeze)이 된다.
  // 신규 항목 persist 는 MU_*_Update 가 없으면 추가(push)하도록 보강해 해결했다.

  /** 부서 선택 토글 반영 (persisted 갱신 → computed 가 반응) */
  const updateDepartmentsSelection = (items: SelectableItem[]) => {
    store.commit('MU_V2Department_Update', { hospitalID: hospitalID(), departments: items })
  }
  /** 직원 선택 토글 반영 */
  const updateEmployeesSelection = (items: SelectableItem[]) => {
    store.commit('MU_V2Employee_Update', { hospitalID: hospitalID(), employees: items })
  }

  return { departments, employees, updateDepartmentsSelection, updateEmployeesSelection }
}
